import { useCallback, useEffect, useRef } from 'react';

/**
 * Integrates a boolean-state modal/dialog with browser history so pressing
 * the back button closes the modal instead of navigating away.
 *
 * Returns:
 *   open()        – open the modal AND push a history entry
 *   close()       – close the modal AND pop the history entry cleanly
 *   onOpenChange  – pass directly to Shadcn Dialog's onOpenChange prop
 */
export function useModalHistory(isOpen, setIsOpen) {
  const pushedRef = useRef(false);
  const suppressRef = useRef(false);
  const isOpenRef = useRef(isOpen);

  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { if (!isOpen) pushedRef.current = false; }, [isOpen]);

  useEffect(() => {
    const handler = () => {
      if (suppressRef.current) { suppressRef.current = false; return; }
      if (isOpenRef.current) { setIsOpen(false); pushedRef.current = false; }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [setIsOpen]);

  const open = useCallback(() => {
    window.history.pushState({ modal: true }, '');
    pushedRef.current = true;
    setIsOpen(true);
  }, [setIsOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    if (pushedRef.current) {
      suppressRef.current = true;
      pushedRef.current = false;
      window.history.back();
    }
  }, [setIsOpen]);

  const onOpenChange = useCallback((o) => { if (!o) close(); }, [close]);

  return { open, close, onOpenChange };
}
